import { useState, useEffect, useCallback } from "react";
import { clientes as apiClientes } from "../../lib/api";
import { formatCurrency } from "../../lib/formato";
import { WalkIcon } from "../Iconos";

const LIMITE = 8;

/**
 * Buscador de cliente del formulario de movimientos.
 *
 * Con la caja vacía muestra a los clientes que ya tuvieron movimiento
 * hoy, que casi siempre son los que se van a repetir. Al escribir,
 * busca en el servidor.
 *
 * A propósito NO permite crear clientes desde aquí: si se pudiera, se
 * crearían sin teléfono. Un cliente nuevo se registra con el botón
 * "Agregar cliente", que pide los datos completos y después abre su
 * ficha para anotarle el movimiento.
 */
const BuscadorCliente = ({ clientesDelDia = [], onElegir }) => {
  const [texto, setTexto] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [clienteDePaso, setClienteDePaso] = useState(null);

  // El cliente de paso: para quien compró una vez y no es cliente del
  // negocio. Se carga una sola vez al abrir el formulario.
  useEffect(() => {
    let vigente = true;
    apiClientes
      .generico()
      .then((datos) => {
        if (vigente) setClienteDePaso(datos);
      })
      .catch(() => {});
    return () => {
      vigente = false;
    };
  }, []);

  const buscar = useCallback(async (termino) => {
    setBuscando(true);
    try {
      const datos = await apiClientes.listar({ busqueda: termino, limite: LIMITE });
      setResultados(datos.items);
    } catch (error) {
      setResultados([]);
    } finally {
      setBuscando(false);
    }
  }, []);

  useEffect(() => {
    if (texto.trim() === "") {
      setResultados([]);
      return;
    }
    const temporizador = setTimeout(() => buscar(texto.trim()), 250);
    return () => clearTimeout(temporizador);
  }, [texto, buscar]);

  const buscandoAlgo = texto.trim() !== "";
  const lista = buscandoAlgo ? resultados : clientesDelDia;

  const nota = (cliente) => {
    if (clientesDelDia.some((c) => String(c.id) === String(cliente.id))) {
      return "movimiento hoy";
    }
    const saldo = Number(cliente.saldo_pendiente);
    if (saldo > 0) return `debe ${formatCurrency(saldo)}`;
    return "sin saldo";
  };

  return (
    <div className="form-group">
      <label className="form-label" htmlFor="busca-cliente">
        Cliente
      </label>
      <input
        id="busca-cliente"
        type="text"
        placeholder="Escriba el nombre o el teléfono..."
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        autoComplete="off"
        autoFocus
      />

      <div className="lista-clientes">
        {lista.length > 0 ? (
          lista.map((cliente) => (
            <button
              key={cliente.id}
              type="button"
              className="opcion-cliente"
              onClick={() => onElegir(cliente)}
            >
              <span>{cliente.nombre_completo}</span>
              <span className="opcion-nota">{nota(cliente)}</span>
            </button>
          ))
        ) : buscando ? (
          <p className="lista-vacia">Buscando...</p>
        ) : buscandoAlgo ? (
          <p className="lista-vacia">
            No hay ningún cliente que coincida con «{texto.trim()}».
            <br />
            Para registrar uno nuevo, cierre esta ventana y use «Agregar cliente».
          </p>
        ) : null}
      </div>

      {clienteDePaso && (
        <button
          type="button"
          className="opcion-de-paso"
          onClick={() => onElegir(clienteDePaso)}
        >
          <span className="de-paso-titulo">
            <WalkIcon /> Cliente de paso
          </span>
          <span className="opcion-nota">para quien no es cliente del negocio</span>
        </button>
      )}

      {!buscandoAlgo && clientesDelDia.length > 0 && (
        <span className="pista">Clientes con movimiento hoy. Escriba para buscar otro.</span>
      )}
    </div>
  );
};

export default BuscadorCliente;
